import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import {
    motion,
    useAnimationControls,
    useInView,
    useReducedMotion,
} from 'framer-motion'

const MOBILE_QUERY = '(max-width: 767px)'

function subscribeMobile(cb) {
    const mq = window.matchMedia(MOBILE_QUERY)
    const handler = () => cb()
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
}

function getMobileSnapshot() {
    return window.matchMedia(MOBILE_QUERY).matches
}

function getServerMobileSnapshot() {
    return false
}

function useIsMobile() {
    return React.useSyncExternalStore(subscribeMobile, getMobileSnapshot, getServerMobileSnapshot)
}

/**
 * Scroll-triggered animation that runs again whenever this block scrolls into view.
 * Uses only transform + opacity (GPU-friendly, no layout shift on mobile).
 *
 * @param {'up'|'down'|'left'|'right'|'fade'|'none'} direction
 * @param {number} delay — seconds (reduced on small screens)
 * @param {number} duration — seconds (faster on small screens)
 */
function ScrollAnimation({
    children,
    className = '',
    style,
    direction = 'up',
    delay = 0,
    duration = 0.55,
}) {
    const ref = useRef(null)
    const isInView = useInView(ref, { once: false, amount: 0.15 })
    const wasInView = useRef(false)
    const didMount = useRef(false)
    const controls = useAnimationControls()
    const isMobile = useIsMobile()
    const prefersReducedMotion = useReducedMotion()

    const { initial, animate } = useMemo(() => {
        const offset = isMobile ? 20 : 48
        const fadeOnly = direction === 'fade' || direction === 'none'

        if (prefersReducedMotion) {
            return {
                initial: { opacity: 0, x: 0, y: 0 },
                animate: { opacity: 1, x: 0, y: 0 },
            }
        }

        if (fadeOnly) {
            return {
                initial: { opacity: 0, x: 0, y: 0 },
                animate: { opacity: 1, x: 0, y: 0 },
            }
        }

        switch (direction) {
            case 'down':
                return {
                    initial: { opacity: 0, x: 0, y: -offset },
                    animate: { opacity: 1, x: 0, y: 0 },
                }
            case 'left':
                return {
                    initial: { opacity: 0, x: offset, y: 0 },
                    animate: { opacity: 1, x: 0, y: 0 },
                }
            case 'right':
                return {
                    initial: { opacity: 0, x: -offset, y: 0 },
                    animate: { opacity: 1, x: 0, y: 0 },
                }
            case 'up':
            default:
                return {
                    initial: { opacity: 0, x: 0, y: offset },
                    animate: { opacity: 1, x: 0, y: 0 },
                }
        }
    }, [direction, isMobile, prefersReducedMotion])

    const transition = useMemo(() => {
        const d = isMobile ? Math.max(0.12, duration * 0.72) : duration
        const del = isMobile ? delay * 0.55 : delay
        return {
            duration: prefersReducedMotion ? 0.15 : d,
            delay: prefersReducedMotion ? 0 : del,
            ease: [0.25, 0.1, 0.25, 1],
        }
    }, [duration, delay, isMobile, prefersReducedMotion])

    useLayoutEffect(() => {
        if (didMount.current) return
        didMount.current = true
        if (!isInView) {
            controls.set(initial)
        }
    }, [isInView, initial, controls])

    useEffect(() => {
        if (isInView && !wasInView.current) {
            controls.set(initial)
            controls.start({ ...animate, transition })
        }
        wasInView.current = isInView
    }, [isInView, controls, initial, animate, transition])

    return (
        <motion.div
            ref={ref}
            className={className}
            initial={false}
            animate={controls}
            style={{
                ...style,
                willChange: 'transform, opacity',
            }}
        >
            {children}
        </motion.div>
    )
}

export default ScrollAnimation
